import { useState } from 'react';
import type { CanvasType, GeneratorMode } from '../types';

type TutorialTopicId =
  | 'led-pattern'
  | 'projection-pattern'
  | 'resolution'
  | 'led-canvas'
  | 'projection-canvas'
  | 'canvas-tools'
  | 'ratio'
  | 'projects';

type TutorialAction =
  | { workspace: 'single'; mode: GeneratorMode }
  | { workspace: 'canvas'; type?: CanvasType }
  | { workspace: 'ratio' };

interface TutorialProps {
  onOpenSingle: (mode: GeneratorMode) => void;
  onOpenCanvas: (type?: CanvasType) => void;
  onOpenRatio: () => void;
}

interface TutorialTopic {
  id: TutorialTopicId;
  category: 'PATTERN' | 'CANVAS' | 'OUTPUT';
  title: string;
  summary: string;
  description: string;
  steps: string[];
  note?: string;
  action: TutorialAction;
  actionLabel: string;
}

const TUTORIAL_TOPICS: TutorialTopic[] = [
  {
    id: 'led-pattern',
    category: 'PATTERN',
    title: 'Build an LED test pattern',
    summary: 'Cabinets, labels and power routes',
    description:
      'Enter the cabinet layout. ScreenGrid calculates the full screen resolution and physical size.',
    steps: [
      'Open Generator and choose LED in the pattern-type switch in the top menu.',
      'Enter the pixel resolution and physical size of one cabinet. Set the number of columns and rows.',
      'Choose a color preset. Enable cabinet coordinates if the installation team needs them. Add a center label or local PNG logo if required.',
      'For power documentation, choose Add cable above the preview and click cabinets in power-flow order. Enter finishes the route. Backspace removes its last point.',
      'Check the calculated full-screen resolution before exporting the PNG.',
    ],
    note: 'The LED raster is calculated from cabinet pixels × cabinet count. The preview does not change its output resolution.',
    action: { workspace: 'single', mode: 'led' },
    actionLabel: 'Open LED Generator',
  },
  {
    id: 'projection-pattern',
    category: 'PATTERN',
    title: 'Build a projection test pattern',
    summary: 'Surface geometry and calibration grid',
    description:
      'Projection mode uses separate values for the signal resolution and the measured image size.',
    steps: [
      'Open Generator and choose Projection in the pattern-type switch in the top menu.',
      'Set the output resolution and measured image width and height in millimetres or metres.',
      'Define the grid by number of cells, cell size in pixels, or physical cell size.',
      'Enable diagonals, center cross, circle and the required color treatment for the alignment task.',
      'Use Follow resolution only when a raster change should proportionally change the recorded physical image size.',
    ],
    note: 'Changing signal resolution does not normally mean the projected surface changed. Leave Follow resolution off in that case.',
    action: { workspace: 'single', mode: 'projection' },
    actionLabel: 'Open Projection Generator',
  },
  {
    id: 'resolution',
    category: 'PATTERN',
    title: 'Set content resolution',
    summary: 'Custom raster, presets and aspect lock',
    description:
      'Content resolution is the native pixel raster of a projection item. Aspect lock keeps the current ratio when width or height changes.',
    steps: [
      'Choose a common preset, or unlock the aspect and enter a completely new width and height.',
      'When both dimensions are correct, enable Lock aspect. ScreenGrid stores the current reduced ratio, including custom ratios.',
      'Change width to recalculate height, or change height to recalculate width. Values are rounded to whole pixels and limited by the browser canvas size.',
      'Selecting a resolution preset also updates the ratio used by the lock.',
      'Canvas places every item at native 1:1 pixels. A 1920 × 1080 item occupies exactly 1920 × 1080 Canvas pixels unless rotated.',
    ],
    note: 'Aspect lock applies to pixel dimensions. Follow resolution controls whether the physical measurements change with them.',
    action: { workspace: 'single', mode: 'projection' },
    actionLabel: 'Open Resolution Controls',
  },
  {
    id: 'led-canvas',
    category: 'CANVAS',
    title: 'Create an LED Canvas',
    summary: 'Build a multi-screen output raster',
    description: 'An LED Canvas places several LED screens inside one fixed output resolution.',
    steps: [
      'Open Canvas, choose LED in the top pattern-type switch, then set the complete Canvas resolution and empty-space background.',
      'Choose New to create an LED screen directly inside the Canvas. Configure its cabinets and pattern, then choose Apply & back to Canvas.',
      'You can also use Add current or add a saved LED project. Added items are independent copies.',
      'Drag screens into place or enter exact X/Y coordinates. LED overlaps show a warning. Content outside the Canvas is clipped on export.',
      'Export a composite PNG, native per-screen files, a complete package, or a Resolume package.',
    ],
    note: 'Canvas items do not include power routes. Labels, logos and pattern settings are retained.',
    action: { workspace: 'canvas', type: 'led' },
    actionLabel: 'Open LED Canvas',
  },
  {
    id: 'projection-canvas',
    category: 'CANVAS',
    title: 'Create a Projection Canvas',
    summary: 'Projectors, overlaps and blend zones',
    description:
      'A Projection Canvas places several projector rasters in one output and measures their overlaps.',
    steps: [
      'Open Canvas and choose Projection in the top pattern-type switch.',
      'Choose New to create and edit a projector in place, or add the current or a saved Projection project.',
      'Add at least two projectors. Overlaps appear as magenta blend zones with their width and height in pixels.',
      'Select two or more projectors using list checkboxes or Ctrl/Cmd/Shift-click in the preview.',
      'Use horizontal or vertical distribution to fill the selected Canvas axis and equalize adjacent blends.',
      'Review To front / To back, rotation and local blend overlays before exporting composite and native projector outputs.',
    ],
    note: 'Distribution can differ by at most 1 px when the available Canvas span cannot be split evenly.',
    action: { workspace: 'canvas', type: 'projection' },
    actionLabel: 'Open Projection Canvas',
  },
  {
    id: 'canvas-tools',
    category: 'CANVAS',
    title: 'Position, align and copy items',
    summary: 'Snapping, exact coordinates and shortcuts',
    description:
      'Move items in the preview or enter exact coordinates in the item settings. Copied items are independent.',
    steps: [
      'Drag an item to snap its edges to the Canvas or other items. Hold Alt while dragging to bypass snapping temporarily.',
      'Use Arrow keys for 1 px movement and Shift + Arrow for 10 px movement.',
      'Set exact X and Y in Selected item → Position. Coordinates refer to the top-left of the occupied rectangle after rotation.',
      'Use Ctrl/Cmd+C and Ctrl/Cmd+V to copy and paste. Repeated pastes are offset by 32 px. Ctrl/Cmd+D duplicates in one step.',
      'Rotate in 90° steps. Canvas does not scale items. Placement stays at 1:1.',
      'For Projection only, multi-select items and use the distribution controls to align the group and equalize blends.',
    ],
    note: 'Copy/paste works within the active Canvas type. LED and Projection items cannot be mixed in one Canvas.',
    action: { workspace: 'canvas' },
    actionLabel: 'Practice in Canvas',
  },
  {
    id: 'ratio',
    category: 'OUTPUT',
    title: 'Preflight a custom AV raster',
    summary: 'Codec alignment and safe dimensions',
    description:
      'RatioGuard checks a target resolution against the selected playback and delivery profiles.',
    steps: [
      'Enter the final target width and height with aspect unlocked.',
      'Enable Lock aspect after the intended ratio is correct. Changing either dimension will recalculate the other.',
      'Select the profiles used in the playback chain. Review errors, recommendations and the GPU texture limit.',
      'Use Pad to keep every source pixel, Crop to stay within the target, or Exact aspect to preserve the ratio.',
    ],
    note: 'RatioGuard is a geometry preflight, not an encoder. Test the final media file on the real playback system.',
    action: { workspace: 'ratio' },
    actionLabel: 'Open RatioGuard',
  },
  {
    id: 'projects',
    category: 'OUTPUT',
    title: 'Save, transfer and export',
    summary: 'Local projects, JSON, PNG and packages',
    description:
      'Drafts and named projects stay in this browser. Use JSON for backup and transfer.',
    steps: [
      'Use Save to create or update a named browser project. Single-screen and Canvas projects are stored separately.',
      'Export configuration JSON before clearing site data or moving to another computer.',
      'Verify the generated filename and native resolution before exporting.',
      'For Canvas, choose composite, separate native outputs, complete package or Resolume package in the export dialog.',
      'ScreenGrid does not upload projects automatically.',
    ],
    action: { workspace: 'single', mode: 'led' },
    actionLabel: 'Open Project Workspace',
  },
];

const LEARNING_PATHS = [
  ['LED pattern', 'Cabinets, labels and power', 0],
  ['Projection pattern', 'Raster and calibration grid', 1],
  ['LED Canvas', 'Multiple LED screens', 3],
  ['Projection Canvas', 'Projectors and blend zones', 4],
] as const;

const FEATURE_SUMMARY = [
  ['01', 'Native output', 'Patterns and Canvas items keep their original pixel dimensions.'],
  ['02', 'Two Canvas types', 'LED and Projection items use separate Canvas modes.'],
  [
    '03',
    'Content geometry',
    'Custom resolution, aspect lock and optional physical-size following.',
  ],
  ['04', 'Precise placement', 'Snapping, X/Y entry, keyboard nudging, rotation and layer order.'],
  [
    '05',
    'Projection blends',
    'Measured overlaps and whole-pixel horizontal or vertical distribution.',
  ],
  ['06', 'Files', 'Local projects, JSON backup, PNG, ZIP and Resolume packages.'],
] as const;

const SHORTCUTS = [
  ['Ctrl/Cmd + Z', 'Undo the last Generator or Canvas change'],
  ['Ctrl/Cmd + Shift + Z', 'Redo the last undone change'],
  ['Mouse wheel', 'Zoom around the cursor'],
  ['Alt + drag', 'Disable Canvas snapping temporarily'],
  ['Arrow keys', 'Move the selected Canvas item or projection group by 1 px'],
  ['Shift + arrows', 'Move the selected Canvas item or projection group by 10 px'],
  ['Ctrl/Cmd/Shift + click', 'Add or remove a projection from the Canvas selection'],
  ['Ctrl/Cmd + C / V', 'Copy and paste the selected Canvas item'],
  ['Ctrl/Cmd + D', 'Duplicate the selected Canvas item'],
  ['Enter', 'Finish the current power route'],
  ['Backspace', 'Remove the last cable point'],
  ['Escape', 'Cancel the current cable draft'],
] as const;

export function Tutorial({ onOpenSingle, onOpenCanvas, onOpenRatio }: TutorialProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [completedTopics, setCompletedTopics] = useState<Set<TutorialTopicId>>(new Set());
  const activeTopic = TUTORIAL_TOPICS[activeIndex] ?? TUTORIAL_TOPICS[0]!;

  const openTopic = (index: number) => {
    setActiveIndex(Math.min(TUTORIAL_TOPICS.length - 1, Math.max(0, index)));
  };

  const advance = () => {
    setCompletedTopics((current) => new Set(current).add(activeTopic.id));
    if (activeIndex < TUTORIAL_TOPICS.length - 1) openTopic(activeIndex + 1);
  };

  const openWorkspace = () => {
    if (activeTopic.action.workspace === 'canvas') onOpenCanvas(activeTopic.action.type);
    else if (activeTopic.action.workspace === 'ratio') onOpenRatio();
    else onOpenSingle(activeTopic.action.mode);
  };

  return (
    <main className="tutorial-page" aria-label="ScreenGrid tutorial">
      <div className="tutorial-content">
        <section className="tutorial-hero">
          <div>
            <p className="tutorial-kicker">SCREENGRID TUTORIAL / 8 TOPICS</p>
            <h1>ScreenGrid operating guide</h1>
            <p className="tutorial-lead">
              Instructions for LED patterns, projection patterns, Canvas layouts, content resolution
              and export.
            </p>
            <div className="tutorial-hero-actions">
              <button type="button" className="button button--primary" onClick={() => openTopic(0)}>
                Start with LED
              </button>
              <button type="button" className="button button--ghost" onClick={() => openTopic(4)}>
                Learn Projection Canvas
              </button>
            </div>
          </div>
          <div className="tutorial-spec" aria-label="Tutorial scope">
            <div>
              <span>WORKFLOWS</span>
              <strong>08</strong>
            </div>
            <div>
              <span>CANVAS TYPES</span>
              <strong>02</strong>
            </div>
            <div>
              <span>PLACEMENT</span>
              <strong>1:1</strong>
            </div>
            <div>
              <span>STORAGE</span>
              <strong>LOCAL</strong>
            </div>
          </div>
        </section>

        <section className="tutorial-paths" aria-labelledby="tutorial-path-title">
          <div className="tutorial-section-heading">
            <p>QUICK START</p>
            <h2 id="tutorial-path-title">Select a workflow</h2>
          </div>
          <div className="tutorial-path-grid">
            {LEARNING_PATHS.map(([title, description, topicIndex], index) => (
              <button key={title} type="button" onClick={() => openTopic(topicIndex)}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{title}</strong>
                <small>{description}</small>
                <em aria-hidden="true">→</em>
              </button>
            ))}
          </div>
        </section>

        <section className="tutorial-guide" aria-label="Interactive workflow guide">
          <nav className="tutorial-topic-nav" aria-label="Tutorial topics">
            {TUTORIAL_TOPICS.map((topic, index) => (
              <button
                key={topic.id}
                type="button"
                className={index === activeIndex ? 'is-active' : ''}
                aria-current={index === activeIndex ? 'step' : undefined}
                onClick={() => openTopic(index)}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <span>
                  <em>{topic.category}</em>
                  <strong>{topic.title}</strong>
                  <small>{topic.summary}</small>
                </span>
                <em>{completedTopics.has(topic.id) ? 'DONE' : ''}</em>
              </button>
            ))}
          </nav>

          <article className="tutorial-lesson">
            <header>
              <p>
                {activeTopic.category} · STEP {String(activeIndex + 1).padStart(2, '0')} /{' '}
                {String(TUTORIAL_TOPICS.length).padStart(2, '0')}
              </p>
              <h2>{activeTopic.title}</h2>
              <span>{activeTopic.description}</span>
            </header>
            <div className="tutorial-lesson-body">
              <div className="tutorial-instructions">
                <ol className="tutorial-step-list">
                  {activeTopic.steps.map((step, index) => (
                    <li key={step}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      <p>{step}</p>
                    </li>
                  ))}
                </ol>
                {activeTopic.note && (
                  <aside className="tutorial-field-note">
                    <strong>NOTE</strong>
                    <span>{activeTopic.note}</span>
                  </aside>
                )}
              </div>
              <TutorialDiagram topic={activeTopic.id} />
            </div>
            <footer className="tutorial-lesson-actions">
              <button
                type="button"
                className="button button--ghost"
                disabled={activeIndex === 0}
                onClick={() => openTopic(activeIndex - 1)}
              >
                Previous
              </button>
              <button type="button" className="button button--primary" onClick={openWorkspace}>
                {activeTopic.actionLabel}
              </button>
              <button type="button" className="button button--ghost" onClick={advance}>
                {activeIndex === TUTORIAL_TOPICS.length - 1 ? 'Mark complete' : 'Next topic'}
              </button>
            </footer>
          </article>
        </section>

        <section className="tutorial-section">
          <div className="tutorial-section-heading">
            <p>CAPABILITY MAP</p>
            <h2>Functions covered</h2>
          </div>
          <div className="tutorial-feature-grid">
            {FEATURE_SUMMARY.map(([number, title, description]) => (
              <article key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="tutorial-reference-grid">
          <article className="tutorial-shortcuts">
            <div className="tutorial-section-heading">
              <p>INPUT REFERENCE</p>
              <h2>Keyboard and pointer controls</h2>
            </div>
            <dl>
              {SHORTCUTS.map(([input, effect]) => (
                <div key={input}>
                  <dt>{input}</dt>
                  <dd>{effect}</dd>
                </div>
              ))}
            </dl>
          </article>
          <article className="tutorial-data-note">
            <div className="tutorial-section-heading">
              <p>DATA &amp; SCOPE</p>
              <h2>Local storage</h2>
            </div>
            <p>
              Drafts, projects and logos are stored in this browser. Clearing site data removes
              them. Export JSON backups for projects you need to keep.
            </p>
            <div className="tutorial-limitations">
              <strong>Canvas limits</strong>
              <span>LED and Projection remain separate</span>
              <span>Native 1:1 placement, no scaling</span>
              <span>Power routes stay in single LED projects</span>
              <span>Warp and final edge blend remain venue-side</span>
            </div>
          </article>
        </section>
      </div>
    </main>
  );
}

function TutorialDiagram({ topic }: { topic: TutorialTopicId }) {
  if (topic === 'projection-pattern') {
    return (
      <div
        className="tutorial-diagram tutorial-diagram--projection"
        aria-label="Projection grid example"
      >
        <DiagramHeader title="PROJECTION PATTERN" value="1920 × 1200" />
        <div className="tutorial-projection-grid">
          <span className="tutorial-projection-cross" />
          <span className="tutorial-projection-circle" />
          <strong>4 × 3 CELLS</strong>
          <small>6000 × 3750 mm</small>
        </div>
        <div className="tutorial-diagram-footer tutorial-geometry-locks">
          <span>CONTENT 16:10</span>
          <span>PHYSICAL 6000 × 3750</span>
        </div>
      </div>
    );
  }
  if (topic === 'resolution') {
    return (
      <div
        className="tutorial-diagram tutorial-diagram--resolution"
        aria-label="Content resolution and aspect lock example"
      >
        <DiagramHeader title="CONTENT RESOLUTION" value="CUSTOM" />
        <div className="tutorial-resolution-panel">
          <div>
            <span>WIDTH / PX</span>
            <strong>3840</strong>
          </div>
          <i>×</i>
          <div>
            <span>HEIGHT / PX</span>
            <strong>1080</strong>
          </div>
          <div className="tutorial-resolution-lock">
            <span>🔒</span>
            <strong>32:9 LOCKED</strong>
            <small>EDIT ONE DIMENSION</small>
          </div>
          <div className="tutorial-resolution-native">
            <span>CANVAS PLACEMENT</span>
            <strong>3840 × 1080 @ 1:1</strong>
          </div>
        </div>
        <div className="tutorial-diagram-footer">
          <span>EXACT REDUCED RATIO</span>
          <span>WHOLE PIXELS</span>
        </div>
      </div>
    );
  }
  if (topic === 'led-canvas') {
    return (
      <div className="tutorial-diagram tutorial-diagram--canvas" aria-label="LED Canvas example">
        <DiagramHeader title="LED CANVAS" value="7680 × 2160" />
        <div className="tutorial-canvas-frame tutorial-canvas-frame--led">
          <div className="tutorial-led-canvas-screen tutorial-led-canvas-screen--a">
            <span>X 0 · Y 0</span>
            <strong>MAIN LED</strong>
            <small>3840 × 1080</small>
          </div>
          <div className="tutorial-led-canvas-screen tutorial-led-canvas-screen--b">
            <span>X 4320 · Y 540</span>
            <strong>IMAG LED</strong>
            <small>1920 × 1080</small>
          </div>
          <i className="tutorial-corner-point" />
        </div>
        <div className="tutorial-diagram-footer">
          <span>2 NATIVE SCREENS</span>
          <span>NO OVERLAP</span>
        </div>
      </div>
    );
  }
  if (topic === 'projection-canvas') {
    return (
      <div
        className="tutorial-diagram tutorial-diagram--canvas"
        aria-label="Projection Canvas example"
      >
        <DiagramHeader title="PROJECTION CANVAS" value="3200 × 800" />
        <div className="tutorial-canvas-frame">
          <div className="tutorial-screen-block tutorial-screen-block--a">
            <span>X 0 · Y 0</span>
            <strong>PROJ 01</strong>
          </div>
          <div className="tutorial-screen-block tutorial-screen-block--b">
            <span>X 960 · Y 0</span>
            <strong>PROJ 02</strong>
          </div>
          <div className="tutorial-screen-block tutorial-screen-block--c">
            <span>X 1920 · Y 0</span>
            <strong>PROJ 03</strong>
          </div>
          <i className="tutorial-blend-zone tutorial-blend-zone--a">320 px</i>
          <i className="tutorial-blend-zone tutorial-blend-zone--b">320 px</i>
        </div>
        <div className="tutorial-diagram-footer">
          <span>3 SELECTED</span>
          <span>EQUAL HORIZONTAL BLENDS</span>
        </div>
      </div>
    );
  }
  if (topic === 'canvas-tools') {
    return (
      <div
        className="tutorial-diagram tutorial-diagram--tools"
        aria-label="Canvas positioning tools"
      >
        <DiagramHeader title="CANVAS OPERATIONS" value="SELECTED: SCREEN 02" />
        <div className="tutorial-tools-panel">
          <div className="tutorial-tools-position">
            <span>POSITION</span>
            <label>
              X <strong>1920</strong>
            </label>
            <label>
              Y <strong>0</strong>
            </label>
          </div>
          <div className="tutorial-tools-keys">
            <kbd>Ctrl</kbd>
            <kbd>C</kbd>
            <i>→</i>
            <kbd>Ctrl</kbd>
            <kbd>V</kbd>
            <span>+32 px offset</span>
          </div>
          <div className="tutorial-tools-actions">
            <span>SNAP: ON</span>
            <span>ROTATE: 90°</span>
            <span>NUDGE: 1 / 10 PX</span>
          </div>
        </div>
        <div className="tutorial-diagram-footer">
          <span>ALT = BYPASS SNAP</span>
          <span>NATIVE SCALE 1:1</span>
        </div>
      </div>
    );
  }
  if (topic === 'projects') {
    return (
      <div className="tutorial-diagram tutorial-diagram--project" aria-label="Project export flow">
        <DiagramHeader title="PROJECT FLOW" value="LOCAL" />
        <div className="tutorial-project-flow">
          <div>
            <span>01</span>
            <strong>Draft</strong>
            <small>automatic</small>
          </div>
          <i />
          <div>
            <span>02</span>
            <strong>Named project</strong>
            <small>IndexedDB</small>
          </div>
          <i />
          <div>
            <span>03</span>
            <strong>JSON / PNG / ZIP</strong>
            <small>user export</small>
          </div>
        </div>
      </div>
    );
  }
  if (topic === 'ratio') {
    return (
      <div className="tutorial-diagram tutorial-diagram--ratio" aria-label="RatioGuard example">
        <DiagramHeader title="RASTER PREFLIGHT" value="PAD / ROUND UP" />
        <div className="tutorial-ratio-flow">
          <div>
            <span>TARGET</span>
            <strong>10989 × 1200</strong>
            <small>ASPECT 3663:400</small>
          </div>
          <i>
            <span>+3 px</span>
          </i>
          <div>
            <span>OUTPUT</span>
            <strong>10992 × 1200</strong>
            <small>DXV / HAP ALIGNED</small>
          </div>
        </div>
        <div className="tutorial-diagram-footer">
          <span>NO CONTENT LOSS</span>
          <span>16 × 16 ALIGNMENT</span>
        </div>
      </div>
    );
  }
  return (
    <div className="tutorial-diagram tutorial-diagram--screen" aria-label="LED screen example">
      <DiagramHeader title="MAIN SCREEN" value="2048 × 1024" />
      <div className="tutorial-led-grid">
        {Array.from({ length: 32 }, (_, index) => (
          <span key={index}>
            {index % 8},{Math.floor(index / 8)}
          </span>
        ))}
        <strong>MAIN SCREEN</strong>
      </div>
      <div className="tutorial-diagram-footer">
        <span>4000 × 2000 mm</span>
        <span>8 × 4 CABINETS</span>
      </div>
    </div>
  );
}

function DiagramHeader({ title, value }: { title: string; value: string }) {
  return (
    <div className="tutorial-diagram-header">
      <span>{title}</span>
      <strong>{value}</strong>
    </div>
  );
}
